import { Fragment, RefObject, memo } from "react";

import type { TMessage } from "@dreamcam/messenger-sdk";

import { useMessengerStore } from "@storage/global/useMessengerStore";

import type { TMessageRefs } from "./ChatContainer";
import { MessageElement } from "./MessageElement";
import { isToday, monthList } from "./helpers";

const RenderUnreadLine = memo(() => (
	<div className="m-2.5 flex items-center justify-center">
		<div className="w-full border-[0.5px] border-msg-time" />
		<div className="mx-4 text-xs font-bold whitespace-pre text-msg-time">New Messages</div>
		<div className="w-full border-[0.5px] border-msg-time" />
	</div>
));

const convertDate = (date: string) => {
	const tempDate = new Date(date);
	const currentDate = new Date();
	if (isToday(currentDate.toDateString(), tempDate.toDateString())) {
		return "Today";
	} else if (currentDate.getFullYear() === tempDate.getFullYear()) {
		return `${tempDate.getDate().toString().padStart(2, "0")} ${monthList[tempDate.getMonth()]}`;
	} else {
		return `${tempDate.getDate().toString().padStart(2, "0")} ${monthList[tempDate.getMonth()]} ${tempDate.getFullYear()}`;
	}
};

interface MessageListProps {
	listMessages: Array<TMessage> | undefined;
	topElementRef: RefObject<HTMLDivElement | null>;
	lastTargetIndex: string;
	lastMessageRef: RefObject<HTMLDivElement>;
	messageCompanionRefs: RefObject<TMessageRefs>;
	firstUnreadMessageId: string;
	isLoadingLoadMoreUp: boolean;
	date: string;
	sectionIndex: number;
	isShowDateSticky: boolean;
}

export const MessageList = memo(
	({
		listMessages,
		topElementRef,
		lastTargetIndex,
		lastMessageRef,
		messageCompanionRefs,
		firstUnreadMessageId,
		isLoadingLoadMoreUp,
		date,
		sectionIndex,
		isShowDateSticky,
	}: MessageListProps) => {
		const { userMsgData } = useMessengerStore();

		return (
			<section>
				<div
					style={isShowDateSticky ? { position: "sticky", top: "0.1875rem" } : { position: "relative" }}
					className={`pointer-events-none sticky z-docked m-2.5 flex h-5.5 justify-center pb-1.5 text-center text-xs
						font-bold text-grey-tertiary transition-opacity duration-300 select-none`}
				>
					{convertDate(date)}
				</div>

				{listMessages
					? listMessages.map((virtualRow, index) => (
							<Fragment key={virtualRow.id}>
								{!isLoadingLoadMoreUp && sectionIndex === 0 && index === Math.floor(listMessages.length / 2) ? (
									<div id="top" ref={topElementRef} />
								) : null}

								<div
									ref={(element: HTMLDivElement) => {
										if (userMsgData.chat_client_id !== virtualRow.sender_id) {
											const updatedMap = messageCompanionRefs.current.set(virtualRow.id, {
												element,
												data: virtualRow,
											});
											messageCompanionRefs.current = updatedMap;
										}
									}}
									style={{
										width: "100%",
									}}
								>
									{firstUnreadMessageId === virtualRow.id ? <RenderUnreadLine /> : null}

									<MessageElement
										messageValue={virtualRow as TMessage}
										isLastElement={virtualRow.id === lastTargetIndex ? lastMessageRef : null}
									/>
								</div>
							</Fragment>
						))
					: null}
			</section>
		);
	},
);
